import React, { useEffect, useState } from 'react';
import {
  Spin,
  Card,
  Row,
  Col,
  Typography,
  Table,
  Select,
  Button,
  Grid,
  Space,
  Avatar,
  Tag,
  Statistic,
  Tooltip,
  DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { dashboardsService } from '../../common/services';
import {
  EyeOutlined,
  CarOutlined,
  UserOutlined,
  TeamOutlined,
  RiseOutlined,
  DollarOutlined,
  SwapOutlined,
  GiftOutlined,
  SettingOutlined,
  DashboardOutlined,
  RightOutlined,
  CalendarOutlined,
  AlertOutlined,
  PhoneOutlined,
  ToolOutlined,
  EnvironmentOutlined,
  RocketOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { vehiculesServices } from '../../common/services';
import { promotionsServices } from '../../common/services';
import { transactionsService } from '../../common/services';
import { partenairesService } from '../../common/services';
import { citiesService } from '../../common/services';
import Can from '../../components/can';

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.locale('fr');

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Defaut: 30 derniers jours
const defaultChartRange = () => [
  dayjs().subtract(29, 'day').startOf('day'),
  dayjs().endOf('day'),
];

// Auto-derive granularite (doit matcher dateRangeHelper.js cote serveur)
const deriveGrain = (range) => {
  if (!range || !range[0] || !range[1]) return 'jour';
  const days = Math.max(1, range[1].diff(range[0], 'day') + 1);
  if (days <= 31)  return 'jour';
  if (days <= 180) return 'semaine';
  if (days <= 730) return 'mois';
  return 'annee';
};

// Construit { from, to } pour les calls API
const rangeToParams = (range) => ({
  from: range[0].format('YYYY-MM-DD'),
  to:   range[1].format('YYYY-MM-DD'),
});

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  let local = phone.replace('+225', '').trim();
  return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

const getAvatarColor = (name) => {
  const colors = ['#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068', '#1890ff', '#eb2f96', '#52c41a'];
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (firstName, lastName) => {
  const f = (firstName || '').charAt(0).toUpperCase();
  const l = (lastName || '').charAt(0).toUpperCase();
  return l + f || 'U';
};

const Home = () => {
  const [partenaireRecent, setPartenaireRecent] = useState(null);
  const [withVehicle, setWithVehicle] = useState([]);
  const [withoutVehicle, setWithoutVehicle] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [rangePromo, setRangePromo] = useState(defaultChartRange());
  const [rangeVehicule, setRangeVehicule] = useState(defaultChartRange());
  const [dataPromo, setDataPromo] = useState([]);
  const [dataVehicules, setDataVehicules] = useState([]);

  const [rangeSosCredit, setRangeSosCredit] = useState(defaultChartRange());
  const [rangeTransferts, setRangeTransferts] = useState(defaultChartRange());
  const [dataSosCredit, setDataSosCredit] = useState([]);
  const [dataTransferts, setDataTransferts] = useState([]);
  const [loadingSosCredit, setLoadingSosCredit] = useState(false);
  const [loadingTransferts, setLoadingTransferts] = useState(false);

  const [rangePartenaires, setRangePartenaires] = useState(defaultChartRange());
  const [dataInscriptionsPartenaires, setDataInscriptionsPartenaires] = useState([]);
  const [loadingPartenaires, setLoadingPartenaires] = useState(false);

  const [rangeClasses, setRangeClasses] = useState(defaultChartRange());
  const [dataClassesServices, setDataClassesServices] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);

  const [rangeOps, setRangeOps] = useState(defaultChartRange());
  const [opsData, setOpsData] = useState([]);
  const [loadingOps, setLoadingOps] = useState(false);

  // Filtre ville global du dashboard
  const [cityFilter, setCityFilter] = useState(undefined);
  const [cities, setCities] = useState([]);

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const isTablet = screens.sm && !screens.lg;
  const chartH = isMobile ? 220 : 280;
  const controlSize = isMobile ? 'small' : 'middle';

  const columns = [
    {
      title: 'Partenaire',
      key: 'user',
      render: (_, record) => {
        const fullName = `${record.lastName || ''} ${record.firstName || ''}`.trim();
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Avatar size={32} style={{ backgroundColor: getAvatarColor(fullName), fontWeight: 600, fontSize: 12 }}>
              {getInitials(record.firstName, record.lastName)}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{fullName}</div>
              <div style={{ fontSize: 11, color: '#8c8c8c' }}>{formatPhoneNumber(record.phone)}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Ville',
      key: 'ville',
      render: (_, record) => {
        const name = record?.city?.name;
        return name
          ? <Tag color="geekblue" style={{ borderRadius: 8, fontSize: 11 }}>{name}</Tag>
          : <span style={{ color: '#bfbfbf', fontSize: 11 }}>—</span>;
      },
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        if (!role) return <span style={{ color: '#bfbfbf', fontSize: 11 }}>—</span>;
        const colorMap = {
          chauffeur: 'green',
          admin: 'red',
          superAdmin: 'volcano',
          directeur: 'magenta',
          support: 'cyan',
          comptable: 'gold',
          commercial: 'blue',
          commercant: 'purple',
          livreur_motolivraison: 'orange',
        };
        return <Tag color={colorMap[role] || 'default'} style={{ borderRadius: 8, fontSize: 11, textTransform: 'capitalize' }}>{role}</Tag>;
      },
    },
    {
      title: 'Date inscription',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Tag color="blue" style={{ borderRadius: 8, fontSize: 11 }}>
          {new Date(date).toLocaleDateString('fr-FR')}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 70,
      render: (_, record) => (
        <Tooltip title="Voir détails">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/dashboard/partenaire/${record._id}`)}
            style={{ borderRadius: 6, background: 'linear-gradient(135deg, #fa8c16, #ffa940)', border: 'none' }}
          />
        </Tooltip>
      ),
    },
  ];

  // Donnees statiques du dashboard (independantes des plages)
  useEffect(() => {
    async function fetchData() {
      try {
        const [recent, w, wo] = await Promise.all([
          dashboardsService.getPartenairesRecent({ cityCode: cityFilter }),
          dashboardsService.getPartenairesThisWeekWithVehicle({ cityCode: cityFilter }),
          dashboardsService.getPartenairesThisWeekWithoutVehicle({ cityCode: cityFilter }),
        ]);
        setPartenaireRecent(recent);
        setWithVehicle(w);
        setWithoutVehicle(wo);
      } catch (error) {
        console.error("Erreur chargement dashboard :", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [cityFilter]);

  useEffect(() => {
    (async () => {
      try {
        const res = await promotionsServices.statsPromotionsDimanche({ ...rangeToParams(rangePromo), cityCode: cityFilter });
        setDataPromo(res?.data || []);
      } catch (e) { console.error(e); }
    })();
  }, [rangePromo, cityFilter]);

  useEffect(() => {
    (async () => {
      try {
        const res = await vehiculesServices.statsInscriptionsVehicules({ ...rangeToParams(rangeVehicule), cityCode: cityFilter });
        setDataVehicules(res?.data || []);
      } catch (e) { console.error(e); }
    })();
  }, [rangeVehicule, cityFilter]);

  useEffect(() => {
    (async () => {
      setLoadingSosCredit(true);
      try {
        const res = await transactionsService.statsSosCredit({ ...rangeToParams(rangeSosCredit), cityCode: cityFilter });
        setDataSosCredit(res?.data || []);
      } catch (e) { console.error(e); } finally { setLoadingSosCredit(false); }
    })();
  }, [rangeSosCredit, cityFilter]);

  useEffect(() => {
    (async () => {
      setLoadingTransferts(true);
      try {
        const res = await transactionsService.statsTransferts({ ...rangeToParams(rangeTransferts), cityCode: cityFilter });
        setDataTransferts(res?.data || []);
      } catch (e) { console.error(e); } finally { setLoadingTransferts(false); }
    })();
  }, [rangeTransferts, cityFilter]);

  useEffect(() => {
    (async () => {
      setLoadingPartenaires(true);
      try {
        const data = await partenairesService.getStatsInscriptions({ ...rangeToParams(rangePartenaires), cityCode: cityFilter });
        setDataInscriptionsPartenaires(data || []);
      } catch (e) { console.error(e); } finally { setLoadingPartenaires(false); }
    })();
  }, [rangePartenaires, cityFilter]);

  useEffect(() => {
    (async () => {
      setLoadingClasses(true);
      try {
        const data = await vehiculesServices.statsClassesServices({ ...rangeToParams(rangeClasses), cityCode: cityFilter });
        setDataClassesServices(data || []);
      } catch (e) { console.error(e); } finally { setLoadingClasses(false); }
    })();
  }, [rangeClasses, cityFilter]);

  useEffect(() => {
    (async () => {
      setLoadingOps(true);
      try {
        const res = await dashboardsService.getOperationsStats({ ...rangeToParams(rangeOps), cityCode: cityFilter });
        setOpsData(res?.data || []);
      } catch (e) { console.error(e); } finally { setLoadingOps(false); }
    })();
  }, [rangeOps, cityFilter]);

  // Charger les villes (pour le filtre global)
  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        const list = Array.isArray(res)
          ? res
          : (res?.data || res?.items || res?.cities || []);
        setCities(list.filter(c => c?.isActive !== false));
      } catch (e) {
        console.error('Erreur chargement villes:', e);
      }
    })();
  }, []);

  const formatDataByCategorie = (rawData) => {
    const lastDate = rawData.length > 0 ? rawData.map(item => item._id.periode).sort().reverse()[0] : null;
    const filtered = rawData.filter(item => item._id.periode === lastDate);
    return filtered.sort((a, b) => b.total - a.total).slice(0, 8).map(item => ({ categorie: item._id.categorie, total: item.total }));
  };
  const formattedCategorieData = formatDataByCategorie(dataClassesServices);

  if (loading || !partenaireRecent) {
    return (
      <div style={{ padding: 40, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="Chargement du tableau de bord..." />
      </div>
    );
  }

  const renderSummary = (data, label) => {
    const total = data.reduce((sum, item) => sum + (item.count || 0), 0);
    return `${label} : ${total.toLocaleString('fr-FR')}`;
  };

  // Raccourcis vers les pages les plus utilisées (filtrés par permission via <Can>)
  const shortcuts = [
    { to: '/dashboard/partenaires',           label: 'Inscriptions TIGOON', icon: <UserOutlined />,            color: '#13c2c2', perm: 'partners:read' },
    { to: '/dashboard/vehicules',             label: 'Véhicules',           icon: <CarOutlined />,             color: '#52c41a', perm: 'vehicles:read' },
    { to: '/dashboard/historique-transferts', label: 'Transferts UV',       icon: <SwapOutlined />,            color: '#722ed1', perm: 'transfers.history:read' },
    { to: '/dashboard/historique-sos-credit', label: 'SOS Crédits',         icon: <AlertOutlined />,           color: '#fa541c', perm: 'soscredit.history:read' },
    { to: '/dashboard/historique-numeros',    label: 'Historique Numéros',  icon: <PhoneOutlined />,           color: '#fa8c16', perm: 'userPhone.history:read' },
    { to: '/dashboard/historique-promotions', label: 'Promo Dimanche',      icon: <GiftOutlined />,            color: '#f5222d', perm: 'promotions:read' },
    { to: '/dashboard/demandes-assistances',  label: 'Assistance',          icon: <CustomerServiceOutlined />, color: '#eb2f96', anyOf: ['assistance:read', 'assistance:update'] },
  ];

  const grainOps = deriveGrain(rangeOps);

  const formatPeriodeTick = (v) => {
    const d = new Date(v);
    if (grainOps === 'mois')  return d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' });
    if (grainOps === 'annee') return d.getFullYear();
    if (grainOps === 'semaine') return `S${dayjs(v).isoWeek()} ${dayjs(v).format('YY')}`;
    return d.toLocaleDateString('fr-FR');
  };

  const tooltipLabel = (v) => {
    const d = new Date(v);
    if (grainOps === 'annee') return `Année ${d.getFullYear()}`;
    if (grainOps === 'mois')  return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    if (grainOps === 'semaine') return `Semaine ${dayjs(v).isoWeek()} - ${dayjs(v).format('YYYY')}`;
    return d.toLocaleString('fr-FR');
  };

  const COLORS = { sos_credit: '#13c2c2', transfert: '#722ed1', changement_numero: '#fa8c16', promo_dimanche: '#f5222d', vehicule_modifie: '#52c41a' };

  // Calculer les totaux des opérations du jour
  const today = new Date().toISOString().split('T')[0]; // Format YYYY-MM-DD
  const todayData = opsData.find(item => {
    const itemDate = new Date(item.periode).toISOString().split('T')[0];
    return itemDate === today;
  });

  const opsTotals = {
    sos_credit: todayData?.byType?.sos_credit || 0,
    transfert: todayData?.byType?.transfert || 0,
    changement_numero: todayData?.byType?.changement_numero || 0,
    promo_dimanche: todayData?.byType?.promo_dimanche || 0,
    vehicule_modifie: todayData?.byType?.vehicule_modifie || 0,
  };

  const ChartRangePicker = ({ value, onChange }) => (
    <RangePicker
      size={controlSize}
      value={value}
      onChange={(vals) => {
        if (!vals || vals.length !== 2 || !vals[0] || !vals[1]) return;
        onChange([vals[0].startOf('day'), vals[1].endOf('day')]);
      }}
      allowClear={false}
      format="DD/MM/YYYY"
      suffixIcon={<CalendarOutlined />}
      style={{ minWidth: isMobile ? 200 : 240 }}
    />
  );

  const ChartCaption = ({ children }) => (
    <div style={{ textAlign: 'center', marginTop: 12, fontSize: 17, fontWeight: 700, color: '#1f1f1f', letterSpacing: 0.2 }}>
      {children}
    </div>
  );

  return (
    <div className="content-wrapper">
      <style>
        {`
          .dashboard-card { border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); transition: all 0.3s; }
          .dashboard-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.1); }
          .dashboard-card .ant-card-head { border-bottom: 2px solid #f0f0f0; }
          .dashboard-card .ant-card-head-title { font-weight: 600; color: #262626; }
          .stat-mini { border-radius: 12px; transition: all 0.3s; }
          .stat-mini:hover { transform: translateY(-2px); }
          .drivers-table .ant-table-row:nth-child(odd) { background-color: #fafafa; }
          .drivers-table .ant-table-row:hover > td { background-color: #fff7e6 !important; }
          .shortcut-item { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 10px; cursor: pointer; transition: all 0.2s; background: #fafafa; outline: none; }
          .shortcut-item:hover, .shortcut-item:focus-visible { background: #fff7e6; transform: translateX(2px); }
          .shortcut-icon { width: 34px; height: 34px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; color: #fff; font-size: 16px; flex-shrink: 0; }
          .shortcut-label { flex: 1; font-weight: 500; font-size: 13px; color: #262626; }
          .shortcut-arrow { color: #bfbfbf; font-size: 11px; }
        `}
      </style>

      <div className="container-xxl flex-grow-1" style={{ paddingTop: 16, paddingLeft: isMobile ? 12 : 24, paddingRight: isMobile ? 12 : 24 }}>
        {/* Filtre ville global */}
        <Row style={{ marginBottom: 16 }} justify="end">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              size={controlSize}
              allowClear
              showSearch
              style={{ width: '100%' }}
              placeholder={
                <span>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />
                  Filtrer le tableau de bord par ville
                </span>
              }
              value={cityFilter}
              onChange={setCityFilter}
              optionFilterProp="label"
              options={cities.map(c => ({ value: c.code, label: c.name }))}
            />
          </Col>
        </Row>

        {/* Quick Stats - Totaux des opérations */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.sos_credit}, #36cfc9)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>SOS Crédit</span>} value={opsTotals.sos_credit} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<AlertOutlined />} />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.transfert}, #b37feb)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>Transferts UV</span>} value={opsTotals.transfert} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<SwapOutlined />} />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.changement_numero}, #ffc069)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>Chgt numéro</span>} value={opsTotals.changement_numero} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<PhoneOutlined />} />
            </Card>
          </Col>
          <Col xs={12} sm={8} lg={4}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.promo_dimanche}, #ff7875)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>Promo dim.</span>} value={opsTotals.promo_dimanche} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<GiftOutlined />} />
            </Card>
          </Col>
          <Col xs={24} sm={16} lg={8}>
            <Card className="stat-mini" style={{ background: `linear-gradient(135deg, ${COLORS.vehicule_modifie}, #95de64)`, border: 'none' }} bodyStyle={{ padding: 16 }}>
              <Statistic title={<span style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11 }}>Véhicules modifiés</span>} value={opsTotals.vehicule_modifie} valueStyle={{ color: 'white', fontSize: 20, fontWeight: 700 }} prefix={<ToolOutlined />} />
            </Card>
          </Col>
        </Row>

        {/* Sync Donut + Tables */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={8}>
            <Card className="dashboard-card" title={<span><RocketOutlined style={{ color: '#fa8c16', marginRight: 8 }} />Raccourcis</span>} bodyStyle={{ padding: 12 }}>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {shortcuts.map((s) => (
                  <Can key={s.to} perm={s.perm} anyOf={s.anyOf || []}>
                    <div
                      className="shortcut-item"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(s.to)}
                      onKeyDown={(e) => { if (e.key === 'Enter') navigate(s.to); }}
                    >
                      <span className="shortcut-icon" style={{ background: s.color }}>{s.icon}</span>
                      <span className="shortcut-label">{s.label}</span>
                      <RightOutlined className="shortcut-arrow" />
                    </div>
                  </Can>
                ))}
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={16}>
            <Card className="dashboard-card" title={<span><CarOutlined style={{ color: '#52c41a', marginRight: 8 }} />Chauffeurs avec véhicule</span>} extra={<Link to="/dashboard/partenaires"><Button type="link" size="small" icon={<RightOutlined />}>Voir tout</Button></Link>} style={{ marginBottom: 16 }}>
              <Table className="drivers-table" dataSource={withVehicle} columns={columns} rowKey="_id" pagination={{ pageSize: 2, hideOnSinglePage: true }} size="small" scroll={{ x: true }} locale={{ emptyText: 'Aucun chauffeur' }} />
            </Card>
            <Card className="dashboard-card" title={<span><UserOutlined style={{ color: '#faad14', marginRight: 8 }} />Chauffeurs sans véhicule</span>} extra={<Link to="/dashboard/partenaires"><Button type="link" size="small" icon={<RightOutlined />}>Voir tout</Button></Link>}>
              <Table className="drivers-table" dataSource={withoutVehicle} columns={columns} rowKey="_id" pagination={{ pageSize: 2, hideOnSinglePage: true }} size="small" scroll={{ x: true }} locale={{ emptyText: 'Aucun chauffeur' }} />
            </Card>
          </Col>
        </Row>

        {/* Charts Row 1 */}
        <Can perm="promotions:stats">
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} lg={8}>
              <Card className="dashboard-card" title={<GiftOutlined style={{ color: '#fa541c' }} />} extra={<ChartRangePicker value={rangePromo} onChange={setRangePromo} />}>
                <ResponsiveContainer width="100%" height={chartH}>
                  <BarChart data={dataPromo}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#fa541c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <ChartCaption>Activations promo dimanche</ChartCaption>
              </Card>
            </Col>
            <Col xs={24} lg={16}>
              <Card className="dashboard-card" title={<RiseOutlined style={{ color: '#1890ff' }} />} extra={<ChartRangePicker value={rangeOps} onChange={setRangeOps} />}>
                <ResponsiveContainer width="100%" height={chartH}>
                  {loadingOps ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                    <BarChart data={opsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="periode" tickFormatter={formatPeriodeTick} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 10 }} />
                      <RechartsTooltip labelFormatter={tooltipLabel} />
                      <Legend wrapperStyle={{ fontSize: 10, paddingTop: 8 }} />
                      <Bar dataKey="byType.sos_credit" name="SOS Crédit" stackId="ops" fill={COLORS.sos_credit} />
                      <Bar dataKey="byType.transfert" name="Transfert" stackId="ops" fill={COLORS.transfert} />
                      <Bar dataKey="byType.changement_numero" name="Chgt numéro" stackId="ops" fill={COLORS.changement_numero} />
                      <Bar dataKey="byType.promo_dimanche" name="Promo dim." stackId="ops" fill={COLORS.promo_dimanche} />
                      <Bar dataKey="byType.vehicule_modifie" name="Véhicule mod." stackId="ops" fill={COLORS.vehicule_modifie} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
                <ChartCaption>Évolution des opérations</ChartCaption>
              </Card>
            </Col>
          </Row>
        </Can>

        {/* Charts Row 2 */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} md={12} lg={8}>
            <Card className="dashboard-card" title={<Link to="/dashboard/partenaires"><TeamOutlined style={{ color: '#13c2c2' }} /></Link>} extra={<ChartRangePicker value={rangePartenaires} onChange={setRangePartenaires} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                {loadingPartenaires ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                  <BarChart data={dataInscriptionsPartenaires}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="total" fill="#13c2c2" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
              <ChartCaption>Inscriptions partenaires</ChartCaption>
            </Card>
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Card className="dashboard-card" title={<Link to="/dashboard/vehicules"><CarOutlined style={{ color: '#52c41a' }} /></Link>} extra={<ChartRangePicker value={rangeVehicule} onChange={setRangeVehicule} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                <BarChart data={dataVehicules}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RechartsTooltip />
                  <Bar dataKey="count" fill="#52c41a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <ChartCaption>Inscriptions véhicules</ChartCaption>
            </Card>
          </Col>
          <Col xs={24} md={24} lg={8}>
            <Card className="dashboard-card" title={<SettingOutlined style={{ color: '#1890ff' }} />} extra={<ChartRangePicker value={rangeClasses} onChange={setRangeClasses} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                {loadingClasses ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                  <BarChart data={formattedCategorieData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="categorie" tick={{ fontSize: 9, angle: -30, textAnchor: 'end' }} interval={0} tickMargin={8} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="total" fill="#1890ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
              <ChartCaption>Catégories véhicules</ChartCaption>
            </Card>
          </Col>
        </Row>

        {/* Charts Row 3 - SOS Credit & Transfers */}
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Card className="dashboard-card" title={<Link to="/dashboard/historique-sos-credit"><DollarOutlined style={{ color: '#1890ff' }} /></Link>} extra={<ChartRangePicker value={rangeSosCredit} onChange={setRangeSosCredit} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                {loadingSosCredit ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                  <BarChart data={dataSosCredit}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#1890ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
              <ChartCaption>Statistiques SOS Crédit</ChartCaption>
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#f6f8fa', borderRadius: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{renderSummary(dataSosCredit, 'Total SOS Crédit')}</Text>
              </div>
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="dashboard-card" title={<Link to="/dashboard/historique-transferts"><SwapOutlined style={{ color: '#722ed1' }} /></Link>} extra={<ChartRangePicker value={rangeTransferts} onChange={setRangeTransferts} />}>
              <ResponsiveContainer width="100%" height={chartH}>
                {loadingTransferts ? <div style={{ textAlign: 'center', paddingTop: 100 }}><Spin /></div> : (
                  <BarChart data={dataTransferts}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#722ed1" radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
              <ChartCaption>Statistiques Transferts</ChartCaption>
              <div style={{ marginTop: 12, padding: '8px 12px', background: '#f6f8fa', borderRadius: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{renderSummary(dataTransferts, 'Total Transferts UV')}</Text>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Home;
